import { useState, type FormEvent } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { AlertBanner, Badge, Button, FormField, Heading, Input, SelectField, Text } from "@lendcord/concord";
import { DocumentUploadSlot } from "@/features/profile/components/DocumentUploadSlot";
import { ProfileField } from "@/features/profile/components/ProfileField";
import { BankAccountSectionSkeleton } from "./BankAccountSectionSkeleton";
import { useGetProfileQuery, useUpsertPrimaryBankDetailMutation } from "@/app/profile/api/profile.api";
import { ACCOUNT_NUMBER_REGEX, IFSC_REGEX } from "@/app/profile/validators";
import type { BankDetail, ProfileDocument } from "@/app/profile/profile.types";

interface BankFormState {
  accountNumber: string;
  ifsc: string;
  accountHolderName: string;
  bankName: string;
  accountType: "SAVINGS" | "CURRENT" | "";
}

const EMPTY_FORM: BankFormState = {
  accountNumber: "",
  ifsc: "",
  accountHolderName: "",
  bankName: "",
  accountType: "",
};

interface BankAccountSectionProps {
  bankDetails: BankDetail[] | null;
  documents: ProfileDocument[] | null;
}

export function BankAccountSection({ bankDetails, documents }: BankAccountSectionProps) {
  const intl = useIntl();
  const { refetch } = useGetProfileQuery();
  const [upsertPrimaryBankDetail] = useUpsertPrimaryBankDetailMutation();

  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState<BankFormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof BankFormState, string>>>({});
  const [formError, setFormError] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!bankDetails) {
    return <BankAccountSectionSkeleton />;
  }

  const primary = bankDetails[0];

  function handleStartEdit() {
    // accountNumber comes back masked from GET /profile, and this endpoint
    // requires the full number on every call (it's a full replace, not a
    // merge) — so it can't be pre-filled, it must be re-entered.
    setForm({
      accountNumber: "",
      ifsc: primary?.ifsc ?? "",
      accountHolderName: primary?.accountHolderName ?? "",
      bankName: primary?.bankName ?? "",
      accountType: (primary?.accountType as BankFormState["accountType"]) ?? "",
    });
    setErrors({});
    setFormError(undefined);
    setIsEditing(true);
  }

  function handleCancel() {
    setIsEditing(false);
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setFormError(undefined);

    const nextErrors: typeof errors = {};
    if (!ACCOUNT_NUMBER_REGEX.test(form.accountNumber)) {
      nextErrors.accountNumber = intl.formatMessage({ id: "lender.profile.accountNumberFormatError" });
    }
    if (!IFSC_REGEX.test(form.ifsc)) {
      nextErrors.ifsc = intl.formatMessage({ id: "lender.profile.ifscFormatError" });
    }
    if (!form.accountHolderName.trim()) {
      nextErrors.accountHolderName = intl.formatMessage({ id: "lender.profile.accountHolderNameRequiredError" });
    }
    if (!form.bankName.trim()) {
      nextErrors.bankName = intl.formatMessage({ id: "lender.profile.bankNameRequiredError" });
    }
    if (!form.accountType) {
      nextErrors.accountType = intl.formatMessage({ id: "lender.profile.accountTypeRequiredError" });
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setIsSubmitting(true);
    try {
      await upsertPrimaryBankDetail({
        accountNumber: form.accountNumber,
        ifsc: form.ifsc,
        accountHolderName: form.accountHolderName.trim(),
        bankName: form.bankName.trim(),
        accountType: form.accountType as "SAVINGS" | "CURRENT",
      }).unwrap();
      await refetch();
      setIsEditing(false);
    } catch {
      setFormError(intl.formatMessage({ id: "lender.profile.bankUpdateError" }));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <Heading level={3}>
        <FormattedMessage id="lender.profile.bankAccountDetailsHeading" />
      </Heading>

      {isEditing ? (
        <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4 rounded-md border border-border p-4">
          {formError && <AlertBanner status="error">{formError}</AlertBanner>}

          <FormField
            label={intl.formatMessage({ id: "lender.profile.accountNumberLabel" })}
            error={errors.accountNumber}
            required
            hint={
              errors.accountNumber ? undefined : intl.formatMessage({ id: "lender.profile.accountNumberEditHint" })
            }
          >
            <Input
              value={form.accountNumber}
              onChange={(event) => setForm({ ...form, accountNumber: event.target.value.replace(/\D/g, "") })}
              maxLength={18}
            />
          </FormField>

          <FormField
            label={intl.formatMessage({ id: "lender.profile.ifscCodeLabel" })}
            error={errors.ifsc}
            required
          >
            <Input
              value={form.ifsc}
              onChange={(event) => setForm({ ...form, ifsc: event.target.value.toUpperCase() })}
              maxLength={11}
            />
          </FormField>

          <FormField
            label={intl.formatMessage({ id: "lender.profile.accountHolderNameLabel" })}
            error={errors.accountHolderName}
            required
          >
            <Input
              value={form.accountHolderName}
              onChange={(event) => setForm({ ...form, accountHolderName: event.target.value })}
            />
          </FormField>

          <FormField
            label={intl.formatMessage({ id: "lender.profile.bankNameLabel" })}
            error={errors.bankName}
            required
          >
            <Input value={form.bankName} onChange={(event) => setForm({ ...form, bankName: event.target.value })} />
          </FormField>

          <SelectField
            label={intl.formatMessage({ id: "lender.profile.accountTypeLabel" })}
            placeholder={intl.formatMessage({ id: "lender.profile.accountTypePlaceholder" })}
            value={form.accountType}
            onValueChange={(value) => setForm({ ...form, accountType: value as BankFormState["accountType"] })}
            error={errors.accountType}
            required
            options={[
              { value: "SAVINGS", label: intl.formatMessage({ id: "lender.profile.accountTypeSavings" }) },
              { value: "CURRENT", label: intl.formatMessage({ id: "lender.profile.accountTypeCurrent" }) },
            ]}
          />

          <div className="flex gap-3">
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <FormattedMessage id="lender.profile.savingButton" />
              ) : (
                <FormattedMessage id="lender.profile.saveButton" />
              )}
            </Button>
            <Button type="button" variant="secondary" onClick={handleCancel} disabled={isSubmitting}>
              <FormattedMessage id="lender.profile.cancelButton" />
            </Button>
          </div>
        </form>
      ) : (
        <div className="rounded-md border border-border p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              {primary?.isPrimary && (
                <Badge status="info">
                  <FormattedMessage id="lender.profile.primaryBadge" />
                </Badge>
              )}
              {primary && (
                <Badge status={primary.isVerified ? "success" : "warning"}>
                  <FormattedMessage
                    id={primary.isVerified ? "lender.profile.verifiedBadge" : "lender.profile.unverifiedBadge"}
                  />
                </Badge>
              )}
            </div>
            <Button type="button" variant="ghost" density="compact" onClick={handleStartEdit}>
              <FormattedMessage id={primary ? "lender.profile.editButton" : "lender.profile.addButton"} />
            </Button>
          </div>

          {primary ? (
            <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-2">
              <ProfileField labelId="lender.profile.bankNameLabel" value={primary.bankName ?? "–"} />
              <ProfileField labelId="lender.profile.accountTypeLabel" value={primary.accountType ?? "–"} />
              <ProfileField labelId="lender.profile.accountNumberLabel" value={primary.accountNumber} />
              <ProfileField labelId="lender.profile.ifscCodeLabel" value={primary.ifsc} />
              <ProfileField
                labelId="lender.profile.accountHolderNameLabel"
                value={primary.accountHolderName ?? "–"}
              />
            </div>
          ) : (
            <Text as="p" size="sm" variant="muted" className="mt-2">
              <FormattedMessage id="lender.profile.noBankDetailsMessage" />
            </Text>
          )}
        </div>
      )}

      <div className="flex flex-col gap-4">
        <Text as="p" size="sm" className="font-medium">
          <FormattedMessage id="lender.profile.documentTypeBankAccount" />
        </Text>
        <DocumentUploadSlot
          type="BANK_ACCOUNT"
          documents={documents?.filter((d) => d.type === "BANK_ACCOUNT") ?? []}
        />
      </div>
    </div>
  );
}
